import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { AppBar } from "../../../components/mobileUi";
import {
  CenterSpinner,
  EmptyText,
  LoadMore,
  M,
  StatCard,
  SummaryCard,
} from "../../../components/managementUi";
import {
  PAYMENT_STATUS,
  UNPAID_LOCKING_HEADERS,
  UnpaidByProjectPanel,
  UnpaidLockingCard,
  unpaidLockingRows,
} from "../../../components/incomeUnpaidUi";
import {
  DesktopBack,
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { formatVnd } from "../../../utils/financeFormat";
import { getManagerUnpaidLockingListApi } from "../../../api/incomeManager.api";
import { EMP_BASE_PATH } from "../../../config";

// Khớp app: app/(feature)/management/income/unpaid/locking-list.tsx
const LIMIT = 10;

const IncomeUnpaidLockingList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { employeeId } = useParams();
  const [search] = useSearchParams();
  const fullName = search.get("fullName") || "";
  const code = search.get("code") || "";

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!employeeId) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getManagerUnpaidLockingListApi({
          employeeId,
          page: p,
          limit: LIMIT,
        });
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setSummary(data?.summary || null);
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") {
          setItems([]);
          setSummary(null);
          setHasNext(false);
        }
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [employeeId]
  );

  useEffect(() => {
    fetchPage(1, "replace");
  }, [fetchPage]);

  const amountByStatus = (s) =>
    summary?.byPaymentStatus?.find((r) => r.paymentStatus === s)?.totalUnpaid || 0;

  const employeeLabel = `${fullName} ${fullName && code ? "-" : ""} ${code}`.trim();

  const openDetail = (item) =>
    navigate(`${EMP_BASE_PATH}/tai-chinh/thu-nhap/chi-tra/${item?._id}`);

  const statCards = [
    {
      subTitle: "GIAO DỊCH",
      title: "Giao dịch chưa trả hết",
      value: summary?.totalTransaction ?? 0,
      icon: appImages.icon_giao_dich,
      bgColor: "#EFF6FF",
      textColor: "#2F80ED",
    },
    {
      subTitle: "DỰ ÁN",
      title: "Số dự án còn tiền",
      value: summary?.totalProject ?? 0,
      icon: appImages.icon_du_an,
      bgColor: "#FFF7ED",
      textColor: "#F57127",
    },
    {
      subTitle: "CHỜ THANH TOÁN",
      title: "Chưa phát sinh chi trả",
      value: formatVnd(amountByStatus(PAYMENT_STATUS.UNPAID)),
      icon: appImages.icon_PDV,
      bgColor: "#F5F3FF",
      textColor: "#8B5CF6",
    },
    {
      subTitle: "ĐANG THANH TOÁN",
      title: "Còn lại của đợt sau",
      value: formatVnd(amountByStatus(PAYMENT_STATUS.PARTIAL)),
      icon: appImages.icon_giai_ngan,
      bgColor: "#FFF7ED",
      textColor: "#F57127",
    },
  ];

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    return (
      <DesktopPage
        title="Giao dịch chờ chi trả"
        subtitle={employeeLabel || undefined}
        right={<DesktopBack onClick={() => navigate(-1)} />}
      >
        <DesktopStatRow
          items={[
            {
              key: "summary",
              title: "TỔNG PDV CHƯA ĐƯỢC CHI TRẢ",
              total: formatVnd(summary?.totalUnpaid || 0),
              color: M.primary,
            },
            ...statCards.map((s) => ({
              key: s.subTitle,
              title: s.title,
              total: String(s.value ?? 0),
              color: s.textColor,
            })),
          ]}
        />

        <DesktopPanel p="0">
          <Text fontSize="16px" fontWeight={600} color={M.baseContent} p="16px">
            Danh sách giao dịch
          </Text>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có giao dịch nào đang chờ chi trả"
            onRowClick={(row) => openDetail(row.raw)}
            rows={unpaidLockingRows(items)}
            headers={UNPAID_LOCKING_HEADERS}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {summary?.byProject?.length ? (
          <DesktopPanel>
            <UnpaidByProjectPanel items={summary.byProject} />
          </DesktopPanel>
        ) : null}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Giao dịch chờ chi trả" />

      <Box px="16px" pt="12px">
        <Box
          mb="12px"
          p="14px"
          borderRadius="12px"
          border="1px solid"
          borderColor={M.border}
          bg="white"
        >
          <Text fontSize="12px" fontWeight={500} color={M.tertiary}>
            NHÂN VIÊN
          </Text>
          <Box h="6px" />
          <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
            {employeeLabel || "-"}
          </Text>
        </Box>

        <SummaryCard
          text="TỔNG PDV CHƯA ĐƯỢC CHI TRẢ"
          total={formatVnd(summary?.totalUnpaid || 0)}
        />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          {statCards.map((s) => (
            <StatCard key={s.subTitle} {...s} />
          ))}
        </Flex>

        <UnpaidByProjectPanel items={summary?.byProject || []} />

        <Text fontSize="14px" fontWeight={600} color={M.baseContent} mt="16px" mb="14px">
          Danh sách giao dịch
        </Text>

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText text="Không có giao dịch nào đang chờ chi trả" />
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it, i) => (
              <UnpaidLockingCard
                key={it?._id || i}
                item={it}
                onClick={() => openDetail(it)}
              />
            ))}
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Flex>
        )}
      </Box>
    </Box>
  );
};

export default IncomeUnpaidLockingList;
